import { supabase } from "@/integrations/supabase/client";
import { BuyerSearchFilters, BuyerSearchResult } from "@/types/buyerSearch";

export const buyerSearchService = {
  searchBuyers: async (
    filters: BuyerSearchFilters = {}, 
    limit = 20, 
    offset = 0
  ): Promise<{ data: BuyerSearchResult[]; count: number; error?: string }> => {
    console.log('buyerSearchService: Starting search with filters:', filters);
    
    try {
      // Get current user (seller) ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: [], count: 0, error: 'User not authenticated' };
      }

      // Use the new market-overlap-based search function
      const { data, error } = await (supabase as any).rpc('search_buyers_with_market_overlap', {
        p_seller_id: user.id,
        p_company_name: filters.companyName || null,
        p_purchasing_types: filters.purchasingTypes || null,
        p_preferred_markets: filters.preferredMarkets || null,
        p_min_deals: filters.dealsAnnuallyMin || null,
        p_max_deals: filters.dealsAnnuallyMax || null,
        p_close_time: filters.averageCloseTime || null,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        console.error('Error searching buyers:', error);
        return { data: [], count: 0, error: error.message };
      }

      // Transform data to match expected types
      const transformedData: BuyerSearchResult[] = (data || []).map((buyer: any) => ({
        ...buyer,
        purchasing_type: Array.isArray(buyer.purchasing_type) ? buyer.purchasing_type : [],
        preferred_markets: Array.isArray(buyer.preferred_markets) ? buyer.preferred_markets : []
      }));

      // For count, get all results for counting (this is a simple approach for now)
      const { data: countData } = await (supabase as any).rpc('search_buyers_with_market_overlap', {
        p_seller_id: user.id,
        p_company_name: filters.companyName || null,
        p_purchasing_types: filters.purchasingTypes || null,
        p_preferred_markets: filters.preferredMarkets || null,
        p_min_deals: filters.dealsAnnuallyMin || null,
        p_max_deals: filters.dealsAnnuallyMax || null,
        p_close_time: filters.averageCloseTime || null,
        p_limit: 10000, // Large number to get all results for count
        p_offset: 0
      });

      const totalCount = (countData || []).length;

      return { data: transformedData, count: totalCount };
    } catch (error) {
      console.error('Error in buyerSearchService.searchBuyers:', error);
      return { data: [], count: 0, error: 'Failed to search buyers' };
    }
  },

  getBuyerProfile: async (buyerId: string): Promise<{ data: BuyerSearchResult | null; error?: string }> => {
    try {
      // Get current user (seller) ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }

      // Use the secure RPC function that respects consent mechanism
      const { data, error } = await (supabase as any).rpc('get_secure_buyer_profile', {
        p_buyer_id: buyerId,
        p_seller_id: user.id
      });

      if (error) {
        console.error('Error fetching buyer profile:', error);
        return { data: null, error: error.message };
      }

      if (!data || data.length === 0) {
        return { data: null, error: 'Buyer profile not found' };
      }

      // Transform data to match expected types
      const buyerData = data[0];
      const transformedData: BuyerSearchResult = {
        ...buyerData,
        purchasing_type: Array.isArray(buyerData.purchasing_type) ? buyerData.purchasing_type : [],
        preferred_markets: Array.isArray(buyerData.preferred_markets) ? buyerData.preferred_markets : []
      };

      return { data: transformedData };
    } catch (error) {
      console.error('Error in buyerSearchService.getBuyerProfile:', error);
      return { data: null, error: 'Failed to fetch buyer profile' };
    }
  },

  getBuyerContactInfo: async (buyerId: string): Promise<{ data: any | null; error?: string }> => {
    try {
      // Get current user (seller) ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: null, error: 'User not authenticated' };
      }

      // Use the secure RPC function that respects consent mechanism
      const { data: buyerData, error } = await (supabase as any).rpc('get_secure_buyer_profile', {
        p_buyer_id: buyerId,
        p_seller_id: user.id
      });

      if (error) {
        console.error('Error fetching buyer contact info:', error);
        return { data: null, error: error.message };
      }

      if (!buyerData || buyerData.length === 0) {
        return { data: null, error: 'Buyer not found or contact information not available' };
      }

      const buyer = buyerData[0];
      
      // Only return contact info if buyer has viewed properties (consent mechanism)
      if (!buyer.has_viewed_properties) {
        // Log denied access attempt
        const { default: SecurityAuditService } = await import('./securityAuditService');
        await SecurityAuditService.logSecurityEvent({
          event_type: 'buyer_contact_access_denied',
          event_details: {
            buyer_id: buyerId,
            buyer_name: buyer.full_name,
            reason: 'buyer_has_not_viewed_properties',
            accessed_via: 'search_results'
          }
        });
        
        return { 
          data: null, 
          error: 'Contact information not available. This buyer has not viewed your properties yet.' 
        };
      }

      // Log successful contact info access
      const { default: SecurityAuditService } = await import('./securityAuditService');
      await SecurityAuditService.logSecurityEvent({
        event_type: 'buyer_contact_info_viewed',
        event_details: {
          buyer_id: buyerId,
          buyer_name: buyer.full_name,
          contact_fields_accessed: ['email', 'phone_number'],
          accessed_via: 'search_results'
        }
      });

      return { 
        data: {
          id: buyer.id,
          user_id: buyer.user_id,
          full_name: buyer.full_name,
          company_name: buyer.company_name,
          phone_number: buyer.phone_number,
          email: buyer.email
        }, 
        error: null 
      };
    } catch (error) {
      console.error('Error in buyerSearchService.getBuyerContactInfo:', error);
      return { data: null, error: 'Failed to fetch buyer contact info' };
    }
  }
};